import { useForm } from 'react-hook-form';
import Alert from '@v2/shared/components/Alert';
import Button from '@v2/shared/components/Button';
import CancelWarningBox from './CancelWarningBox';
import { CancelReason } from './types';

/**
 * Why a customer is leaving, in their words as far as a radio group allows.
 *
 * The answer is not only telemetry: "renewing too soon" is a problem a delay solves, and
 * that reason alone routes to the delay form instead of the retention offer. See
 * DisableAutoRenewalModal.
 */
const CANCEL_REASONS: { value: CancelReason; label: string }[] = [
  { value: 'found_lower_price', label: 'Found a lower price elsewhere' },
  { value: 'too_expensive', label: 'Too expensive / Budget reasons' },
  { value: 'not_interested', label: 'Not interested in this content anymore' },
  { value: 'renewing_too_soon', label: 'Renewing too soon' },
  { value: 'other', label: 'Other' },
];

type Props = {
  autoRenewServiceName: string;
  onContinueWithSurvey: (reason: CancelReason) => void;
  onDisable: () => void;
  loading?: boolean;
  /** The disable request's failure — CancelWarningBox can send one from this step. */
  error?: Error;
};

export default function CancelSurveyForm({
  autoRenewServiceName,
  onContinueWithSurvey,
  onDisable,
  loading = false,
  error,
}: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ reason: CancelReason }>();

  return (
    <div className="p-5 md:p-8">
      <h2 className="font-semibold text-2xl mb-4 tracking-tight text-center">Mind Telling Us Why?</h2>
      <p className="mb-6 font-medium text-center">
        We&apos;re sorry you want to disable your {autoRenewServiceName} subscription. If you&apos;re willing to tell us
        why, we can make it right for you.
      </p>

      <form onSubmit={handleSubmit(({ reason }) => onContinueWithSurvey(reason))} noValidate>
        <fieldset className="border-0 p-0 m-0 mb-6">
          <legend className="font-semibold text-sm mb-3 p-0">
            Why do you want to disable {autoRenewServiceName}?
          </legend>

          <div className="space-y-3">
            {CANCEL_REASONS.map((reason) => (
              <label key={reason.value} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value={reason.value}
                  className="mr-3 h-4 w-4 accent-red-600 border-gray-300"
                  {...register('reason', { required: 'Please select a reason' })}
                />
                <span className="text-sm">{reason.label}</span>
              </label>
            ))}
          </div>

          {errors.reason && <p className="text-red-600 text-sm mt-2">{errors.reason.message}</p>}
        </fieldset>

        <div className="flex justify-center mb-4">
          <Button type="submit" className="text-sm">
            Continue
          </Button>
        </div>
      </form>

      {error && <Alert color="danger">{error.message}</Alert>}

      <CancelWarningBox
        autoRenewServiceName={autoRenewServiceName}
        onDisable={onDisable}
        loading={loading}
      />
    </div>
  );
}
